<template>
  <Dialog
    v-model:visible="visible"
    header="Send to FSL"
    :modal="true"
    :style="{ width: '600px' }"
    @hide="resetForm"
  >
    <div class="fsl-send-form">
      <div v-if="unsavedQuantityWarning" class="warning-box">
        <i class="ri-error-warning-line"></i>
        <span>The quantity shown is from your form and may not be saved yet. <strong>Save the form first</strong> (click Update/Submit), then send to FSL, or the request may fail.</span>
      </div>

      <div class="info-box mb-2">
        <i class="ri-information-line"></i>
        Property: <strong>{{ property?.property_description || '-' }}</strong>
      </div>

      <div class="info-box">
        <i class="ri-information-line"></i>
        {{ isEditMode ? 'Sent Property:' : 'Send Property:' }} <strong>{{ displayQuantityLabel }}</strong>
      </div>

      <div class="form-group">
        <label>Send Option:</label>
        <div class="send-option-row">
          <label class="send-option-item">
            <input type="radio" v-model="form.send_option" value="full" @change="onSendOptionChange" />
            <span>Send Entire Property</span>
          </label>
          <label class="send-option-item">
            <input type="radio" v-model="form.send_option" value="partial" @change="onSendOptionChange" />
            <span>Send Partial Property</span>
          </label>
        </div>
      </div>

      <div class="form-group" v-if="form.send_option === 'partial'">
        <label>Quantity to Send:</label>
        <div class="quantity-input-row">
          <input
            type="number"
            class="form-control qty-input"
            v-model.number="form.partial_quantity"
            :min="isWeightBased ? 0.001 : 1"
            :step="isWeightBased ? 0.001 : 1"
            placeholder="Enter quantity"
          />
          <div v-if="isWeightBased" class="qty-radio-row">
            <label class="send-option-item">
              <input type="radio" v-model="form.partial_unit" value="kg" />
              <span>kg</span>
            </label>
            <label class="send-option-item">
              <input type="radio" v-model="form.partial_unit" value="g" />
              <span>gm</span>
            </label>
          </div>
          <div v-else class="qty-unit-static">nos.</div>
        </div>
      </div>

      <div class="form-group" v-if="form.send_option === 'partial' || isEditMode">
        <label>Balance Quantity:</label>
        <input
          type="number"
          class="form-control"
          v-model.number="form.balance_quantity"
          :min="0"
          :step="isWeightBased ? 0.001 : 1"
        />
      </div>

      <div class="form-group">
        <label>Date of Sending: *</label>
        <Calendar
          v-model="form.date_sent"
          dateFormat="yy-mm-dd"
          class="w-full"
          :maxDate="new Date()"
        />
      </div>

      <div class="form-group">
        <label>FSL Acknowledgement No.:</label>
        <InputText
          v-model="form.acknowledgement_no"
          placeholder="Enter acknowledgement number (if available)"
          class="w-full"
        />
      </div>

      <div class="form-group">
        <div class="checkbox-row">
          <Checkbox v-model="markAsReceived" :binary="true" inputId="fsl-received-checkbox" />
          <label for="fsl-received-checkbox" class="mb-0">FSL received date available</label>
        </div>
      </div>

      <div class="form-group" v-if="markAsReceived">
        <label>FSL Received Date: *</label>
        <Calendar
          v-model="form.date_received"
          dateFormat="yy-mm-dd"
          class="w-full"
          :maxDate="new Date()"
        />
      </div>

      <div class="form-group">
        <label>Remarks:</label>
        <Textarea
          v-model="form.remarks"
          rows="3"
          placeholder="Enter any additional notes"
          class="w-full"
        />
      </div>
    </div>

    <template #footer>
      <Button
        label="Cancel"
        icon="ri-close-line"
        @click="visible = false"
        class="p-button-text"
      />
      <Button
        :label="submitButtonLabel"
        icon="ri-send-plane-line"
        @click="submitFSL"
        :loading="loading"
      />
    </template>
  </Dialog>
</template>

<script>
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import Calendar from 'primevue/calendar';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import Checkbox from 'primevue/checkbox';

export default {
  name: 'SendToFSLDialog',
  components: {
    Dialog,
    Button,
    Calendar,
    InputText,
    Textarea,
    Checkbox
  },
  props: {
    property: {
      type: Object,
      required: true
    },
    availableQuantity: {
      type: Number,
      required: true
    },
    serverAvailableQuantity: {
      type: Number,
      default: null
    },
    movements: {
      type: Array,
      default: () => []
    },
    fslBatches: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      visible: false,
      loading: false,
      markAsReceived: false,
      form: {
        date_sent: new Date(),
        date_received: null,
        acknowledgement_no: '',
        remarks: '',
        send_option: 'full',
        partial_quantity: null,
        partial_unit: 'kg',
        balance_quantity: null
      },
      currentFslId: null,
      currentSentQuantity: 0,
      previousPartialUnit: 'kg'
    };
  },
  computed: {
    unsavedQuantityWarning() {
      if (this.serverAvailableQuantity == null) return false;
      return this.availableQuantity > this.serverAvailableQuantity;
    },
    isWeightBased() {
      const name = (this.property?.property_type?.property_type || this.property?.PropertyType?.property_type || '').toString().toLowerCase();
      const keywords = ['drug', 'gold', 'silver', 'diamond', 'precious metal', 'narcotic', 'grams', 'contraband'];
      return keywords.some(kw => name.includes(kw));
    },
    latestFslSendRecord() {
      return (this.movements || []).find(m => m.type === 'fsl_send' && m.fsl_detail) || null;
    },
    isEditMode() {
      return !!this.latestFslSendRecord;
    },
    displayQuantity() {
      if (this.isEditMode && this.currentSentQuantity > 0) return this.currentSentQuantity;
      return this.availableQuantity;
    },
    existingRecordIsPartial() {
      return !!this.latestFslSendRecord?.fsl_detail?.is_partial;
    },
    displayQuantityLabel() {
      const partialWithoutQtyInCreate =
        !this.isEditMode &&
        this.form.send_option === 'partial' &&
        this.getQuantityToSend() <= 0;
      const partialWithoutQtyInEdit =
        this.isEditMode &&
        this.existingRecordIsPartial &&
        this.currentSentQuantity <= 0;
      if (partialWithoutQtyInCreate || partialWithoutQtyInEdit) return 'PARTIAL';
      return this.formatForDisplay(this.displayQuantity);
    },
    submitButtonLabel() {
      if (!this.isEditMode) return 'Send to FSL';
      if (this.markAsReceived && this.form.date_received) return 'Update & Complete';
      return 'Update FSL';
    },
    balanceQuantity() {
      if (this.isEditMode || this.form.send_option !== 'partial') return 0;
      return this.toBaseQuantity(this.form.balance_quantity);
    }
  },
  methods: {
    open() {
      this.visible = true;
      this.resetForm();
      this.prefillFromExistingRecord();
    },
    resetForm() {
      this.markAsReceived = false;
      this.form = {
        date_sent: new Date(),
        date_received: null,
        acknowledgement_no: '',
        remarks: '',
        send_option: 'full',
        partial_quantity: null,
        partial_unit: this.getDefaultWeightUnit(),
        balance_quantity: null
      };
      this.previousPartialUnit = this.form.partial_unit;
      this.currentFslId = null;
      this.currentSentQuantity = 0;
    },
    prefillFromExistingRecord() {
      const latest = this.latestFslSendRecord;
      if (!latest || !latest.fsl_detail) return;
      this.currentFslId = latest.fsl_detail.fsl_id || null;
      this.currentSentQuantity = Number(latest.fsl_detail.quantity_sent || latest.quantity_moved || 0);
      this.form.date_sent = this.toDateOrNow(latest.fsl_detail.date_sent || latest.movement_date);
      this.form.acknowledgement_no = latest.fsl_detail.acknowledgement_no || '';
      this.form.remarks = latest.remarks || '';
      this.form.send_option = latest.fsl_detail.is_partial ? 'partial' : 'full';
      const rawBalance = latest.fsl_detail.balance_quantity;
      const sentQty = Number(latest.fsl_detail.quantity_sent || latest.quantity_moved || 0);
      const shouldKeepBalanceEmpty =
        latest.fsl_detail.is_partial &&
        sentQty <= 0 &&
        (rawBalance === null || Number(rawBalance) === 0);
      this.form.balance_quantity = shouldKeepBalanceEmpty
        ? null
        : (rawBalance != null ? this.fromBaseQuantity(rawBalance) : null);
      if (latest.fsl_detail.date_received) {
        this.markAsReceived = true;
        this.form.date_received = this.toDateOrNow(latest.fsl_detail.date_received);
      }
    },
    toDateOrNow(value) {
      if (!value) return new Date();
      const dt = new Date(value);
      if (isNaN(dt.getTime())) return new Date();
      return dt;
    },
    formatQuantity(qty) {
      const grams = Number(qty) || 0;
      const kilograms = grams / 1000;
      const gramsText = Number.isInteger(grams) ? `${grams}` : `${Number(grams.toFixed(3))}`;
      return `${kilograms.toFixed(3)} KG (${gramsText} GM)`;
    },
    formatForDisplay(qty) {
      return this.isWeightBased ? this.formatQuantity(qty) : `${Math.floor(Number(qty) || 0)} nos.`;
    },
    getQuantityToSend() {
      if (this.isEditMode || this.form.send_option === 'full') {
        return Number(this.availableQuantity) || 0;
      }
      return this.toBaseQuantity(this.form.partial_quantity);
    },
    toBaseQuantity(value, unit = this.form.partial_unit) {
      const n = Number(value);
      if (!isFinite(n) || n <= 0) return 0;
      if (this.isWeightBased) {
        return unit === 'kg' ? n * 1000 : n;
      }
      return Math.floor(n);
    },
    getOptionalBalanceQuantity() {
      if (this.form.send_option !== 'partial') return null;
      const raw = this.form.balance_quantity;
      if (raw === '' || raw === null || typeof raw === 'undefined') return null;
      const n = Number(raw);
      if (!isFinite(n)) return null;
      return this.toBaseQuantity(n);
    },
    fromBaseQuantity(value, unit = this.form.partial_unit) {
      const n = Number(value) || 0;
      if (this.isWeightBased) {
        const v = unit === 'kg' ? n / 1000 : n;
        return Number(v.toFixed(3));
      }
      return Math.floor(n);
    },
    onSendOptionChange() {
      if (this.form.send_option !== 'partial') return;
      this.form.partial_quantity = null;
      this.form.balance_quantity = null;
      this.previousPartialUnit = this.form.partial_unit;
    },
    getDefaultWeightUnit() {
      return Number(this.availableQuantity || 0) >= 1000 ? 'kg' : 'g';
    },
    async submitFSL() {
      const quantityToSend = this.getQuantityToSend();

      if (!this.isEditMode && (!this.availableQuantity || this.availableQuantity <= 0)) {
        this.$store.dispatch('error', 'No quantity available at PS');
        return;
      }
      if (!this.isEditMode && this.form.send_option === 'partial') {
        // Quantity is optional for partial; if left blank we persist as partial with 0 sent.
      }
      if (!this.isEditMode && quantityToSend > Number(this.availableQuantity || 0)) {
        this.$store.dispatch('error', 'Partial quantity cannot exceed available quantity at PS');
        return;
      }

      if (!this.form.date_sent) {
        this.$store.dispatch('error', 'Please select a date');
        return;
      }

      if (this.markAsReceived && !this.form.date_received) {
        this.$store.dispatch('error', 'Please select FSL received date');
        return;
      }

      this.loading = true;
      try {
        if (this.isEditMode) {
          if (!this.currentFslId) {
            this.$store.dispatch('error', 'Unable to identify existing FSL record');
            return;
          }
          await this.$store.dispatch('post', {
            uri: 'updateFSLDetail',
            data: {
              property_id: this.property.property_id,
              fsl_id: this.currentFslId,
              date_sent: this.formatDate(this.form.date_sent),
              date_received: this.markAsReceived ? this.formatDate(this.form.date_received) : null,
              is_partial: this.form.send_option === 'partial',
              balance_quantity: this.getOptionalBalanceQuantity(),
              acknowledgement_no: this.form.acknowledgement_no,
              remarks: this.form.remarks
            }
          });
          this.$store.dispatch('success', this.markAsReceived ? 'FSL details updated and completed successfully' : 'FSL details updated successfully');
        } else {
          await this.$store.dispatch('post', {
            uri: 'sendPropertyToFSL',
            data: {
              property_id: this.property.property_id,
              quantity_sent: quantityToSend,
              is_partial: this.form.send_option === 'partial',
              balance_quantity: this.getOptionalBalanceQuantity(),
              date_sent: this.formatDate(this.form.date_sent),
              date_received: this.markAsReceived ? this.formatDate(this.form.date_received) : null,
              acknowledgement_no: this.form.acknowledgement_no,
              remarks: this.form.remarks,
              sync_available_at_ps: this.availableQuantity
            }
          });
          this.$store.dispatch('success', 'Property sent to FSL successfully');
        }
        this.$emit('refresh');
        this.visible = false;
      } catch (error) {
        const msg = error.response?.data?.message || '';
        if (msg && (msg.includes('Insufficient quantity') || msg.includes('insufficient'))) {
          this.$store.dispatch('error', 'Insufficient quantity available at PS. If you recently updated the quantity in the form, please save the form first (click Update/Submit), then try sending to FSL again.');
        } else {
          this.$store.dispatch('error', msg || 'Failed to send property to FSL');
        }
      } finally {
        this.loading = false;
      }
    },
    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  }
}
</script>

<style scoped>
.fsl-send-form {
  padding: 10px 0;
}

.warning-box {
  background: #FFF3E0;
  border: 1px solid #FF9800;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 16px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: #E65100;
  font-size: 14px;
}

.warning-box i {
  font-size: 20px;
  flex-shrink: 0;
}

.info-box {
  background: #E3F2FD;
  border: 1px solid #2196F3;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: #1976D2;
}

.info-box i {
  font-size: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}

.checkbox-row {
  display: flex;
  gap: 10px;
  align-items: center;
}

.send-option-row {
  display: flex;
  gap: 20px;
  align-items: center;
  flex-wrap: wrap;
}

.send-option-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 400;
  margin: 0;
}

.quantity-input-row {
  display: flex;
  gap: 10px;
  align-items: center;
}

.qty-input {
  flex: 1;
}

.qty-unit {
  width: 90px;
}

.qty-radio-row {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  min-width: 110px;
}

.qty-radio-row .send-option-item {
  gap: 0;
}

.qty-radio-row .send-option-item input[type="radio"] {
  margin-right: 4px;
}

.qty-unit-static {
  min-width: 50px;
  font-weight: 500;
  color: #555;
}

.w-full {
  width: 100%;
}
</style>
